import React, { useState, useRef, useEffect } from "react";
import { Search, Camera, UserCheck, X, UserX, Clock } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import "../../css/AttendanceTake.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function AttendanceTake() {
  const [activeTab, setActiveTab] = useState("automatic");
  const [activeUserType, setActiveUserType] = useState("student");
  const [isScannerActive, setIsScannerActive] = useState(false);
  const [scannedUsers, setScannedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastScanned, setLastScanned] = useState(null);
  const [todaysAttendance, setTodaysAttendance] = useState({});
  const html5QrcodeScannerRef = useRef(null);
  const isProcessingRef = useRef(false); 

  useEffect(() => {
    if (activeTab !== "automatic" && isScannerActive) {
      stopScanner();
    }
  }, [activeTab]);


  useEffect(() => {
    if (activeTab === "manual") {
      fetchUsers();
      fetchTodaysAttendance();
    }
  }, [activeTab, activeUserType]);

  useEffect(() => {
    return () => {
      if (html5QrcodeScannerRef.current) {
        stopScanner();
      }
    };
  }, []);

  const fetchTodaysAttendance = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const response = await fetch(
        `https://schoolpro-api.uniums.com/api/attendance/today/${activeUserType}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch attendance data");
      }

      const data = await response.json();
      const attendanceMap = {};
      data.forEach((item) => {
        attendanceMap[item.userId] = item.status;
      });

      setTodaysAttendance(attendanceMap);
    } catch (error) {
      console.error("Fetch Attendance Error:", error);
    }
  };

  const fetchUsers = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please login again.");
      return;
    }

    setIsLoading(true);
    try {
      const endpoint =
        activeUserType === "student" ? "/api/students" : "/api/teachers";
      const response = await fetch(`https://schoolpro-api.uniums.com${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch users");
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setUsers(data);
        if (data.length === 0) {
          toast.info(`No ${activeUserType}s found`);
        }
      } else {
        throw new Error("Invalid data format received");
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      toast.error(error.message);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };
  const startScanner = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Your browser doesn't support camera access");
      }
      setIsScannerActive(true);
      setTimeout(async () => {
        try {
          const qrReaderContainer = document.getElementById("qr-reader-container");
          if (!qrReaderContainer) {
            throw new Error("Scanner container not found in DOM");
          }
          if (html5QrcodeScannerRef.current) {
            try {
              await html5QrcodeScannerRef.current.stop();
              html5QrcodeScannerRef.current = null;
            } catch (stopError) {
              console.warn("Error stopping previous scanner:", stopError);
              html5QrcodeScannerRef.current = null;
            }
          }
          toast.info("Starting camera, please allow camera access...");
          const html5QrCode = new Html5Qrcode("qr-reader-container");
          html5QrcodeScannerRef.current = html5QrCode;

          const qrCodeSuccessCallback = async (decodedText) => {
            if (isProcessingRef.current) {
              return;
            }
            
            try {
              isProcessingRef.current = true;
              await handleQRCodeDetection(decodedText);
              setTimeout(() => {
                isProcessingRef.current = false;
              }, 1000);
              
            } catch (err) {
              console.error("Error in QR code processing:", err);
              toast.error(`Error: ${err.message || "QR code processing failed"}`);
              isProcessingRef.current = false;
            }
          };

          const qrCodeErrorCallback = (error) => {
            if (error && error.message && !error.message.includes("No QR code found")) {
              console.warn("QR scanner error:", error);
            }
          };

          const config = {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
            disableFlip: false,
            experimentalFeatures: {
              useBarCodeDetectorIfSupported: true
            }
          };
          await html5QrCode.start(
            { facingMode: "environment" },
            config,
            qrCodeSuccessCallback,
            qrCodeErrorCallback
          );

          toast.success("QR code scanner started successfully");
        } catch (error) {
          console.error("Error starting scanner:", error);
          toast.error(`Scanner error: ${error.message || "Failed to initialize scanner"}`);
          setIsScannerActive(false);
          if (html5QrcodeScannerRef.current) {
            try {
              await html5QrcodeScannerRef.current.stop();
              html5QrcodeScannerRef.current = null;
            } catch (e) {
              console.warn("Error cleaning up scanner:", e);
              html5QrcodeScannerRef.current = null;
            }
          }
        }
      }, 500);
    } catch (error) {
      console.error("Scanner initialization error:", error);
      toast.error(`Failed to initialize scanner: ${error.message || "Unknown error"}`);
      setIsScannerActive(false);
    }
  };
  const stopScanner = async () => {
    if (!html5QrcodeScannerRef.current) {
      setIsScannerActive(false);
      return;
    }
    
    try {
      isProcessingRef.current = true; 
      await html5QrcodeScannerRef.current.stop();
      toast.info("Scanner stopped");
    } catch (error) {
      console.error("Error stopping scanner:", error);
    } finally {
      html5QrcodeScannerRef.current = null;
      isProcessingRef.current = false;
      setIsScannerActive(false);
    }
  };

  const handleQRCodeDetection = async (qrCodeValue) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token missing. Please log in again.");
      return;
    }
    toast.info(`Processing QR code...`);
    try {
      const response = await fetch(
        `https://schoolpro-api.uniums.com/api/attendance/scan`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ barcodeValue: qrCodeValue }),
        }
      );

      const data = await response.json();
      if (response.ok) {
        const userWithTimestamp = {
          ...data.user,
          scannedTime: new Date().toLocaleTimeString(),
          status: "present",
        };
        setScannedUsers((prev) => {
          const existingIndex = prev.findIndex((u) => u._id === data.user._id);
          if (existingIndex >= 0) {
            const updatedList = [...prev];
            updatedList[existingIndex] = userWithTimestamp;
            return updatedList;
          } else {
            return [userWithTimestamp, ...prev];
          }
        });
        setLastScanned(data.user);
        toast.success(
          `Attendance marked for ${data.user.firstName} ${data.user.lastName}`
        );
      } else {
        toast.error(data.message || "Failed to mark attendance");
      }
    } catch (error) {
      console.error("QR code processing error:", error);
      toast.error(`Failed to process QR code: ${error.message}`);
    }
  };

  const handleManualAttendance = async (userId, status = "present") => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `https://schoolpro-api.uniums.com/api/attendance/manual`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId,
            userType: activeUserType,
            status,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to mark attendance");
      }

      toast.success(
        `${status === "present" ? "Present" : "Absent"} marked successfully`
      );
      setTodaysAttendance((prev) => ({
        ...prev,
        [userId]: status,
      }));
    } catch (error) {
      toast.error(error.message);
    }
  };
  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
      if (activeTab === "automatic" && isScannerActive) {
        stopScanner();
      }
    }
  };
  const handleUserTypeChange = (userType) => {
    if (userType !== activeUserType) {
      if (isScannerActive) {
        stopScanner();
      }
      setActiveUserType(userType);
    }
  };

  const getStatusDisplay = (userId) => {
    if (!todaysAttendance[userId]) return null;

    if (todaysAttendance[userId] === "present") {
      return {
        text: "Present",
        color: "#28a745",
        icon: <UserCheck size={16} />,
      };
    } else {
      return { text: "Absent", color: "#dc3545", icon: <UserX size={16} /> };
    }
  };

  return (
    <div className="attendancetake-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="attendancetake-header">
        <h2>Take Attendance</h2>
        <div className="attendancetake-tabs">
          <button
            className={`attendancetake-tab ${
              activeTab === "automatic" ? "active" : ""
            }`}
            onClick={() => handleTabChange("automatic")}
          >
            Automatic (QR Code)
          </button>
          <button
            className={`attendancetake-tab ${
              activeTab === "manual" ? "active" : ""
            }`}
            onClick={() => handleTabChange("manual")}
          >
            Manual
          </button>
        </div>
      </div>

      <div className="attendancetake-usertype">
        <button
          className={`attendancetake-usertype-btn ${
            activeUserType === "student" ? "active" : ""
          }`}
          onClick={() => handleUserTypeChange("student")}
        >
          Students
        </button>
        <button
          className={`attendancetake-usertype-btn ${
            activeUserType === "teacher" ? "active" : ""
          }`}
          onClick={() => handleUserTypeChange("teacher")}
        >
          Teachers
        </button>
      </div>

      {activeTab === "automatic" ? (
        <div className="attendancetake-scanner-container">
          <div className="attendancetake-scanner">
            {!isScannerActive ? (
              <button
                className="attendancetake-start-btn"
                onClick={startScanner}
              >
                <Camera size={24} />
                Start QR Scanner
              </button>
            ) : (
              <>
                <div
                  id="qr-reader-container"
                  className="attendancetake-viewport"
                  style={{
                    width: "100%",
                    height: "300px",
                    position: "relative",
                    border: "2px solid #ddd",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                />
                <button
                  className="attendancetake-stop-btn"
                  onClick={stopScanner}
                >
                  <X size={20} />
                  Stop Scanner
                </button>
                <div className="scanner-instructions">
                  <p>Hold the QR code steady in the scanner frame</p>
                  {lastScanned && (
                    <div className="last-scanned-user">
                      Last scanned: {lastScanned.firstName}{" "}
                      {lastScanned.lastName}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="attendancetake-scanned-list">
            <h3>Scanned Users</h3>
            {scannedUsers.length === 0 ? (
              <div className="attendancetake-no-scans">
                No users scanned yet. Scan ID cards to mark attendance.
              </div>
            ) : (
              scannedUsers.map((user, index) => (
                <div
                  key={`${user._id}-${index}`}
                  className="attendancetake-scanned-item"
                >
                  <div className="attendancetake-scanned-user">
                    <span>
                      {user.firstName} {user.lastName}
                    </span>
                    <span
                      className="attendancetake-status"
                      style={{
                        color:
                          user.status === "present" ? "#28a745" : "#dc3545",
                      }}
                    >
                      {user.status === "present" ? "Present" : "Absent"}
                    </span>
                  </div>
                  <span className="attendancetake-time">
                    <Clock size={14} />
                    {user.scannedTime}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="attendancetake-manual">
          <div className="attendancetake-search">
            <Search size={20} />
            <input
              type="text"
              placeholder={`Search ${activeUserType}s...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {isLoading ? (
            <div className="attendancetake-loading">Loading users...</div>
          ) : (
            <div className="attendancetake-users-list">
              {users
                .filter(
                  (user) =>
                    `${user.firstName} ${user.lastName}`
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                    (activeUserType === "student" &&
                      user.rollNumber &&
                      user.rollNumber
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())) ||
                    (activeUserType === "teacher" &&
                      user.employeeId &&
                      user.employeeId
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()))
                )
                .map((user) => {
                  const statusDisplay = getStatusDisplay(user._id);

                  return (
                    <div key={user._id} className="attendancetake-user-item">
                      <div className="attendancetake-user-info">
                        <span className="attendancetake-name">
                          {user.firstName} {user.lastName}
                        </span>
                        <span className="attendancetake-id">
                          {activeUserType === "student"
                            ? user.rollNumber
                            : user.employeeId}
                        </span>
                        {statusDisplay && (
                          <span
                            className="attendancetake-status-badge"
                            style={{ color: statusDisplay.color }}
                          >
                            {statusDisplay.icon}
                            {statusDisplay.text}
                          </span>
                        )}
                      </div>
                      <div className="attendancetake-btns-container">
                        <button
                          className="attendancetake-mark-btn present"
                          onClick={() =>
                            handleManualAttendance(user._id, "present")
                          }
                          disabled={todaysAttendance[user._id] === "present"}
                        >
                          <UserCheck size={20} />
                          Present
                        </button>
                        <button
                          className="attendancetake-mark-btn absent"
                          onClick={() =>
                            handleManualAttendance(user._id, "absent")
                          }
                          disabled={todaysAttendance[user._id] === "absent"}
                        >
                          <UserX size={20} />
                          Absent
                        </button>
                      </div>
                    </div>
                  );
                })}

              {users.length === 0 && !isLoading && (
                <div className="attendancetake-no-users">
                  No {activeUserType}s found. Please add some {activeUserType}s
                  first.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AttendanceTake;